/**
 * CameraCard — one camera/screenshot tile on the site dashboard.
 *
 * Shows a single camera as a clean image surface — latest capture (or an
 * empty state) with the capture and settings controls overlaid on the image.
 * The card intentionally keeps one compact layout at every breakpoint so it
 * can sit in the relay grid with the same footprint as relay cards.
 *
 * Reads plain-JS camera objects from the new odl-app contract (unit.cic.cameras[]).
 * Looks up CIC images from the entity store (keyed by camera id).
 * Action gating comes from `canEdit` / `canAddSchedule` capability props.
 *
 * Camera config edit/delete and camera schedule edit are settings-area concerns
 * (out of scope for the dashboard pass). The settings gear opens CameraDialog as
 * before; schedule management opens CameraSchedulesBlade.
 *
 * Contains:
 * - CameraCard — the per-camera card (default export)
 */
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, Button, Card, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getKnownRelationPresence } from '@sophocles/features/entities/site';
import { loadCameraImages } from '@sophocles/features/media/api';
import { selectImagesByCamera } from '@sophocles/features/media/slice';
import CameraDialog from '@sophocles/views/sites/components/CameraCards/CameraDialog';
import CameraPicturePopup from '@sophocles/views/sites/components/CameraCards/CameraPicturePopup';

const CARD_MIN_HEIGHT = 410;

// A capture at least this wide relative to its height earns a second dashboard
// unit. Sits above 16:9 (1.78) so ordinary widescreen cameras keep a single
// unit; the panoramic 4-Series frames (about 3.5:1) clear it.
export const ULTRAWIDE_ASPECT_RATIO = 2;

// White-on-image control affordances sit over a dark scrim so they read on
// any photo (and on the empty placeholder, which is also dark).
const scrimSx = {
  position: 'absolute',
  left: 0,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  px: 1,
  py: 0.5,
  color: 'common.white',
};

/**
 * @param {object}    props
 * @param {Immutable.Map} props.site          - selected site (Immutable, for legacy CameraPicturePopup)
 * @param {object}    props.cic              - plain JS cic object from unit.cic
 * @param {object}    props.camera           - plain JS camera object from unit.cic.cameras[]
 * @param {object}    props.params           - route params { cust_id, sitegroup_id, site_id }
 * @param {boolean}   [props.isVantage]      - whether this is a Vantage screenshot
 * @param {boolean}   [props.isStandAlone]   - whether the CIC is standalone (vs addon)
 * @param {Function}  props.onOpenSchedules  - opens the schedules blade for this camera
 * @param {boolean}   [props.canCapture]     - capabilities.can_capture_camera (gates the capture popup)
 * @param {boolean}   [props.canEdit]        - capabilities.can_edit_camera (gates the settings gear)
 * @param {boolean}   [props.canAddSchedule] - capabilities.can_add_cameraschedule
 * @param {Function} [props.onAspectRatioChange] - called with (cameraId, aspectRatio) once the
 *                                                latest capture decodes, so the dashboard can
 *                                                size this camera's grid cell
 *
 * NOTE: CameraPicturePopup is a legacy class component that still expects an Immutable
 * site Map for site.get('id'). The site prop flows through as-is; camera is now plain-JS
 * (the fromJS shim has been removed).
 */
export default function CameraCard({
  site,
  cic,
  camera,
  params,
  isVantage,
  isStandAlone,
  onOpenSchedules,
  canCapture,
  canEdit,
  canAddSchedule,
  onAspectRatioChange,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const cameraId = camera.id ?? null;
  const cameraNumberStr = String(cameraId ?? '');

  const siteId = params.site_id;
  const cicHasRadio = isStandAlone ? getKnownRelationPresence(site.get('cic', null), 'radio') : false;

  useEffect(() => {
    if (cameraId != null) {
      // Load the most-recent image for this camera. Binary-files actor_id is the
      // camera id for cic-image rows (entity_id is the site scope); also filter
      // by site to stay within scope.
      dispatch(loadCameraImages({ siteId, queryParams: { actor_id: cameraId, limit: 1, ordering: '-timestamp' } }));
    }
  }, [dispatch, cameraId, siteId]);

  // Pull images from the media store (keyed by String(camera.id)).
  const imagesByCamera = useSelector(selectImagesByCamera);
  const cameraImages = imagesByCamera.get(cameraNumberStr);
  const lastImage =
    cameraImages && !cameraImages.isEmpty() ? cameraImages.sortBy(img => img.get('timestamp')).last() : null;

  // The capture's aspect ratio is only knowable from the decoded image —
  // neither the camera record nor the binary-file row carries dimensions.
  // Measured through a callback ref rather than `onLoad` alone: a cached
  // capture is already decoded by the time React attaches handlers, so its
  // load event never fires and the card would keep the standard cell.
  // naturalWidth is 0 until decode completes, which is when onLoad follows.
  const measureAspectRatio = useCallback(
    node => {
      if (!node || cameraId == null) return;
      const { naturalWidth, naturalHeight } = node;
      if (!naturalWidth || !naturalHeight) return;
      onAspectRatioChange?.(cameraId, naturalWidth / naturalHeight);
    },
    [cameraId, onAspectRatioChange],
  );

  const cameraNumber = camera.camera_number;
  const title = isVantage ? t('sites.screenshot') : `${t('sites.camera')} ${cameraNumber}`;
  const scheduleCount = (camera.schedules ?? []).length;

  const mediaPath = `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/media?tab=images&mode=list`;

  const imageSurface = (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: 'grey.900', overflow: 'hidden' }}>
      {lastImage ? (
        // Absolutely positioned so the image's intrinsic size never drives
        // the card's height — the card is auto-height, so an in-flow img's
        // percentage height resolves to intrinsic and a portrait capture
        // would balloon the card (and its whole grid row). The stage keeps
        // the card's own footprint; tall frames letterbox via `contain`.
        <Box
          component="img"
          src={lastImage.get('image')}
          alt={title}
          ref={measureAspectRatio}
          onLoad={event => measureAspectRatio(event.target)}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (
        <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ position: 'absolute', inset: 0 }}>
          <ImageNotSupportedOutlinedIcon sx={{ color: 'grey.600', fontSize: 32 }} />
          <Typography variant="caption" sx={{ color: 'grey.500' }}>
            {t('sites.noImages', 'No images')}
          </Typography>
        </Stack>
      )}

      {/* Top scrim: title + capture/settings controls */}
      <Box
        sx={{ ...scrimSx, top: 0, gap: 0.5, background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0))' }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
          {title}
        </Typography>
        {canCapture === true && (
          <CameraPicturePopup camera={camera} site={site} isStandAlone={isStandAlone} isVantage={isVantage} />
        )}
        {canEdit && (
          <Tooltip title={t('sites.cameraSettings', 'Camera Settings')}>
            <IconButton size="small" onClick={() => setSettingsOpen(true)} sx={{ color: 'common.white' }}>
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Bottom scrim: last capture time */}
      {lastImage && (
        <Box
          sx={{
            ...scrimSx,
            bottom: 0,
            justifyContent: 'flex-end',
            background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))',
          }}
        >
          <Typography variant="caption" noWrap>
            {t('media.lastImageTime', 'Last image')} {moment(lastImage.get('timestamp')).fromNow()}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Card sx={{ height: '100%', minHeight: CARD_MIN_HEIGHT, display: 'flex', flexDirection: 'column' }}>
      {/* Image stage — grey so any letterbox margin reads as a frame */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          bgcolor: 'grey.900',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
        }}
      >
        {imageSurface}
      </Box>

      {/* Footer: schedules blade trigger + gallery link */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 0.5, borderTop: 1, borderColor: 'divider', mt: 'auto' }}
      >
        <Button
          size="small"
          startIcon={<ScheduleOutlinedIcon />}
          onClick={() => onOpenSchedules(camera)}
          sx={{ textTransform: 'none' }}
        >
          {t('general.schedules', 'Schedules')}
          {scheduleCount > 0 ? ` (${scheduleCount})` : ''}
        </Button>
        <Button size="small" onClick={() => navigate(mediaPath)} sx={{ textTransform: 'none' }}>
          {t('media.viewAll', 'View all')}
        </Button>
      </Stack>

      {settingsOpen && (
        <CameraDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          siteId={siteId}
          camera={camera}
          canEdit={canEdit ?? false}
          canDelete={false}
          cic={cic}
          cicHasRadio={cicHasRadio}
          isStandAlone={isStandAlone}
          isVantage={isVantage}
        />
      )}
    </Card>
  );
}

CameraCard.propTypes = {
  /** Immutable site Map — legacy CameraPicturePopup reads site.get('id'). */
  site: PropTypes.object.isRequired,
  /** Plain JS cic object from unit.cic. */
  cic: PropTypes.object.isRequired,
  /** Plain JS camera object from unit.cic.cameras[]. */
  camera: PropTypes.object.isRequired,
  /** Route params: { cust_id, sitegroup_id, site_id }. */
  params: PropTypes.object.isRequired,
  isVantage: PropTypes.bool,
  isStandAlone: PropTypes.bool,
  /** Opens the schedules blade for this camera. */
  onOpenSchedules: PropTypes.func.isRequired,
  /** capabilities.can_capture_camera — gates the capture popup */
  canCapture: PropTypes.bool,
  /** capabilities.can_edit_camera — gates the settings gear */
  canEdit: PropTypes.bool,
  /** capabilities.can_add_cameraschedule */
  canAddSchedule: PropTypes.bool,
  /** Reports (cameraId, aspectRatio) once the latest capture has decoded. */
  onAspectRatioChange: PropTypes.func,
};
